import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { getImageUrl } from '../../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_MB = 10;
const ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';

const ImageUploader = ({ value, onChange, label = "Image", onUploadingChange = () => {} }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Fichier trop lourd (max ${MAX_MB} Mo)`);
      return;
    }
    const auth = localStorage.getItem('admin_auth');
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    onUploadingChange(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload`, form, {
        headers: {
          Authorization: `Basic ${auth}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      onChange(res.data.url);
      toast.success('Image uploadée !');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur upload';
      toast.error(msg);
    } finally {
      setUploading(false);
      onUploadingChange(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const preview = value ? getImageUrl(value) : null;

  return (
    <div className="space-y-2">
      <label className="block text-xs uppercase tracking-wider text-[#B9B2A8]">{label}</label>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`relative rounded-lg border-2 border-dashed transition ${
          dragging ? 'border-[#C9A84C] bg-[#C9A84C]/5' : 'border-white/15 bg-white/5'
        }`}
      >
        {preview ? (
          <div className="flex items-center gap-4 p-3">
            <img
              src={preview}
              alt="preview"
              className="w-24 h-24 object-cover rounded-md border border-white/10"
              onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[#B9B2A8] truncate">{value}</div>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => inputRef.current?.click()}
                  className="border-white/15 text-[#F5F0E8] hover:bg-white/5"
                  disabled={uploading}
                >
                  {uploading ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Upload className="w-3 h-3 mr-2" />}
                  Remplacer
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange('')}
                  className="text-[#B9B2A8] hover:text-red-400"
                  disabled={uploading}
                >
                  <X className="w-3 h-3 mr-1" /> Retirer
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-full flex flex-col items-center justify-center gap-2 py-8 text-[#B9B2A8] hover:text-[#F5F0E8] transition"
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-[#C9A84C]" />
            ) : (
              <Upload className="w-6 h-6 text-[#C9A84C]" />
            )}
            <span className="text-sm">
              {uploading ? 'Upload en cours…' : 'Cliquer ou glisser une image ici'}
            </span>
            <span className="text-xs text-[#B9B2A8]/60">
              JPG, PNG, WebP, GIF · max {MAX_MB} Mo
            </span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={handleFileSelect}
        className="hidden"
      />

      <div className="text-xs text-[#B9B2A8]/70">
        ou colle une URL externe :
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… ou /uploads/…"
          className="mt-1 block w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-[#F5F0E8] focus:outline-none focus:border-[#C9A84C]/50"
        />
      </div>
    </div>
  );
};

export default ImageUploader;
